package com.patronat.top.client.presentation.home.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.GridItemSpan
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items as gridItems
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.patronat.top.dto.ReviewMediaRef
import com.patronat.top.dto.ReviewResponse
import com.patronat.top.dto.ReviewSummaryResponse
import com.patronat.top.dto.VenueResponse
import com.patronat.top.dto.venues.DayOfWeek as VenueDayOfWeek
import com.patronat.top.dto.venues.VenueMediaRef
import com.patronat.top.ui.atoms.AppButton
import com.patronat.top.ui.atoms.AppNetworkImage
import com.patronat.top.ui.atoms.ButtonType
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.AccentPink
import com.patronat.top.ui.theme.ActiveStatusStroke
import com.patronat.top.ui.theme.BodyMedium
import com.patronat.top.ui.theme.MineShaft100
import com.patronat.top.ui.theme.MineShaft200
import com.patronat.top.ui.theme.MineShaft400
import com.patronat.top.ui.theme.MineShaft50
import com.patronat.top.ui.theme.MineShaft500
import com.patronat.top.ui.theme.MineShaft600
import com.patronat.top.ui.theme.MineShaft900
import com.patronat.top.ui.theme.MineShaft950
import com.patronat.top.ui.theme.Primary600
import com.patronat.top.ui.theme.White
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.stringResource
import top.clientapp.generated.resources.Res
import top.clientapp.generated.resources.client_review_atmosphere
import top.clientapp.generated.resources.client_review_personal
import top.clientapp.generated.resources.client_review_price
import top.clientapp.generated.resources.client_review_quality
import top.clientapp.generated.resources.client_review_speed
import top.clientapp.generated.resources.client_review_title
import top.clientapp.generated.resources.client_reviews_empty
import top.clientapp.generated.resources.client_reviews_latest
import top.clientapp.generated.resources.client_reviews_latest_desktop
import top.clientapp.generated.resources.client_reviews_rating_details
import top.clientapp.generated.resources.client_reviews_unknown_author
import top.clientapp.generated.resources.client_venue_back_content_description
import top.clientapp.generated.resources.client_venue_closed
import top.clientapp.generated.resources.client_venue_leave_review
import top.clientapp.generated.resources.client_venue_status_closes_at
import top.clientapp.generated.resources.client_venue_status_opens_at
import top.clientapp.generated.resources.client_venue_status_open

@Composable
fun VenueReviewsContent(
    venue: VenueResponse,
    reviews: List<ReviewResponse>,
    summary: ReviewSummaryResponse?,
    isLoading: Boolean,
    selectedPhotoUrl: String?,
    likingReviewIds: Set<String>,
    onBackClick: () -> Unit,
    onPhotoClick: (String) -> Unit,
    onPhotoDismiss: () -> Unit,
    onLikeClick: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    val reviewPhotoUrls = remember(reviews) {
        reviews
            .flatMap { it.photos }
            .mapNotNull { it.publicUrl?.takeIf(String::isNotBlank) }
            .distinct()
            .shuffled()
            .take(12)
    }

    Box(
        modifier = modifier
            .fillMaxSize()
            .background(White)
    ) {
        LazyColumn(
            modifier = Modifier.fillMaxSize(),
            contentPadding = PaddingValues(start = 24.dp, top = 18.dp, end = 24.dp, bottom = 34.dp),
            verticalArrangement = Arrangement.spacedBy(18.dp)
        ) {
            item {
                ReviewsTopBar(onBackClick = onBackClick)
            }

            if (reviewPhotoUrls.isNotEmpty()) {
                item {
                    ReviewPhotoHero(
                        photoUrls = reviewPhotoUrls,
                        onPhotoClick = onPhotoClick
                    )
                }
            }

            item {
                VenueReviewsHeader(venue = venue)
            }

            item {
                ReviewRatingBlock(
                    summary = summary,
                    venue = venue
                )
            }

            item {
                Text(
                    text = stringResource(Res.string.client_reviews_latest),
                    style = BodyMedium.copy(
                        color = MineShaft900,
                        fontSize = 15.sp,
                        lineHeight = 22.5.sp,
                        fontWeight = FontWeight.Medium
                    )
                )
            }

            if (isLoading && reviews.isEmpty()) {
                item {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(120.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator(color = Primary600)
                    }
                }
            } else if (reviews.isEmpty()) {
                item {
                    Text(
                        text = stringResource(Res.string.client_reviews_empty),
                        style = BodyMedium.copy(
                            color = MineShaft500,
                            fontSize = 15.sp,
                            lineHeight = 22.5.sp,
                            fontWeight = FontWeight.Normal,
                            textAlign = TextAlign.Center
                        ),
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(vertical = 28.dp)
                    )
                }
            } else {
                items(
                    items = reviews,
                    key = { it.id }
                ) { review ->
                    VenueReviewCard(
                        review = review,
                        isLikeLoading = review.id in likingReviewIds,
                        onPhotoClick = onPhotoClick,
                        onLikeClick = onLikeClick
                    )
                }
            }
        }

        selectedPhotoUrl?.let { url ->
            ReviewPhotoPreviewDialog(
                url = url,
                onDismiss = onPhotoDismiss
            )
        }
    }
}

@Composable
fun VenueReviewsDesktopContent(
    venue: VenueResponse,
    reviews: List<ReviewResponse>,
    summary: ReviewSummaryResponse?,
    isLoading: Boolean,
    selectedPhotoUrl: String?,
    likingReviewIds: Set<String>,
    isFavorite: Boolean,
    onBackClick: () -> Unit,
    onReviewClick: () -> Unit,
    onFavoriteClick: () -> Unit,
    onPhotoClick: (String) -> Unit,
    onPhotoDismiss: () -> Unit,
    onLikeClick: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    val gallery = remember(venue.gallery) {
        venue.gallery.filter { !it.publicUrl.isNullOrBlank() }
    }

    Box(
        modifier = modifier
            .fillMaxSize()
            .background(White)
    ) {
        LazyVerticalGrid(
            columns = GridCells.Adaptive(minSize = 390.dp),
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 24.dp),
            contentPadding = PaddingValues(top = 88.dp, bottom = 24.dp),
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            item(span = { GridItemSpan(maxLineSpan) }) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .widthIn(max = 1392.dp)
                ) {
                    ReviewsDesktopTopBar(onBackClick = onBackClick)

                    Spacer(modifier = Modifier.height(20.dp))

                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .heightIn(min = 278.dp)
                            .clip(RoundedCornerShape(10.dp))
                            .border(1.dp, MineShaft100, RoundedCornerShape(10.dp))
                            .background(White)
                            .padding(24.dp),
                        horizontalArrangement = Arrangement.spacedBy(44.dp),
                        verticalAlignment = Alignment.Top
                    ) {
                        ReviewsDesktopGallery(
                            gallery = gallery,
                            title = venue.name,
                            modifier = Modifier.width(383.dp)
                        )

                        ReviewsDesktopVenueInfo(
                            venue = venue,
                            onReviewClick = onReviewClick,
                            modifier = Modifier.width(328.dp)
                        )

                        ReviewsDesktopRatingBlock(
                            summary = summary,
                            venue = venue,
                            modifier = Modifier.weight(1f)
                        )

                        Box(
                            modifier = Modifier
                                .size(40.dp)
                                .clip(CircleShape)
                                .clickable(onClick = onFavoriteClick),
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(
                                painter = if (isFavorite) AppIcons.HeartFilled else AppIcons.Heart,
                                contentDescription = null,
                                tint = Color.Unspecified,
                                modifier = Modifier.size(27.dp)
                            )
                        }
                    }

                    Spacer(modifier = Modifier.height(18.dp))

                    Text(
                        text = stringResource(Res.string.client_reviews_latest_desktop),
                        style = BodyMedium.copy(
                            color = MineShaft900,
                            fontSize = 16.sp,
                            lineHeight = 24.sp,
                            fontWeight = FontWeight.Medium
                        )
                    )
                }
            }

            if (isLoading && reviews.isEmpty()) {
                item(span = { GridItemSpan(maxLineSpan) }) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(160.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator(color = Primary600)
                    }
                }
            } else if (reviews.isEmpty()) {
                item(span = { GridItemSpan(maxLineSpan) }) {
                    Text(
                        text = stringResource(Res.string.client_reviews_empty),
                        style = BodyMedium.copy(
                            color = MineShaft500,
                            fontSize = 15.sp,
                            lineHeight = 22.5.sp,
                            fontWeight = FontWeight.Normal,
                            textAlign = TextAlign.Center
                        ),
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(vertical = 28.dp)
                    )
                }
            } else {
                gridItems(
                    items = reviews,
                    key = { it.id }
                ) { review ->
                    VenueReviewCard(
                        review = review,
                        isLikeLoading = review.id in likingReviewIds,
                        onPhotoClick = onPhotoClick,
                        onLikeClick = onLikeClick
                    )
                }
            }
        }

        selectedPhotoUrl?.let { url ->
            ReviewPhotoPreviewDialog(
                url = url,
                onDismiss = onPhotoDismiss
            )
        }
    }
}

@Composable
private fun ReviewsDesktopTopBar(onBackClick: () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(10.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .size(38.dp)
                .clip(RoundedCornerShape(12.dp))
                .clickable(onClick = onBackClick),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                painter = AppIcons.ArrowLeft,
                contentDescription = stringResource(Res.string.client_venue_back_content_description),
                tint = MineShaft950,
                modifier = Modifier.size(26.dp)
            )
        }

        Text(
            text = stringResource(Res.string.client_review_title),
            style = BodyMedium.copy(
                color = MineShaft900,
                fontSize = 28.sp,
                lineHeight = 42.sp,
                fontWeight = FontWeight.Medium
            )
        )
    }
}

@Composable
private fun ReviewsDesktopGallery(
    gallery: List<VenueMediaRef>,
    title: String,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier.height(258.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        ReviewsDesktopImage(
            imageUrl = gallery.firstOrNull()?.publicUrl,
            title = title,
            modifier = Modifier
                .width(296.dp)
                .height(258.dp)
        )

        Column(
            modifier = Modifier
                .width(79.dp)
                .height(258.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            gallery.drop(1).take(3).forEach { media ->
                ReviewsDesktopImage(
                    imageUrl = media.publicUrl,
                    title = title,
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(78.dp)
                )
            }
        }
    }
}

@Composable
private fun ReviewsDesktopImage(
    imageUrl: String?,
    title: String,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .clip(RoundedCornerShape(8.dp))
            .background(MineShaft50),
        contentAlignment = Alignment.Center
    ) {
        if (imageUrl.isNullOrBlank()) {
            Icon(
                painter = AppIcons.VenueImagePlaceholder,
                contentDescription = null,
                tint = Color.Unspecified,
                modifier = Modifier.size(54.dp)
            )
        } else {
            AppNetworkImage(
                model = imageUrl,
                contentDescription = title,
                contentScale = ContentScale.Crop,
                modifier = Modifier.fillMaxSize()
            )
        }
    }
}

@Composable
private fun ReviewsDesktopVenueInfo(
    venue: VenueResponse,
    onReviewClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Column(modifier = modifier) {
        Text(
            text = venue.name,
            style = BodyMedium.copy(
                color = MineShaft900,
                fontSize = 28.sp,
                lineHeight = 42.sp,
                fontWeight = FontWeight.Medium,
                letterSpacing = (-0.12).sp
            ),
            maxLines = 2,
            overflow = TextOverflow.Ellipsis
        )

        Spacer(modifier = Modifier.height(6.dp))

        Text(
            text = venue.location.address,
            style = BodyMedium.copy(
                color = MineShaft600,
                fontSize = 14.sp,
                lineHeight = 24.sp,
                fontWeight = FontWeight.Normal,
                letterSpacing = (-0.31).sp
            ),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )

        Spacer(modifier = Modifier.height(8.dp))

        ReviewsDesktopWorkStatus(venue = venue)

        Spacer(modifier = Modifier.height(8.dp))

        Row(
            horizontalArrangement = Arrangement.spacedBy(5.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                painter = AppIcons.RatingStarFilled,
                contentDescription = null,
                tint = Color.Unspecified,
                modifier = Modifier.size(14.dp)
            )

            Text(
                text = venue.ratingSummaryText(),
                style = BodyMedium.copy(
                    color = MineShaft600,
                    fontSize = 13.sp,
                    lineHeight = 18.sp,
                    fontWeight = FontWeight.Normal
                )
            )
        }

        Spacer(modifier = Modifier.height(20.dp))

        AppButton(
            text = stringResource(Res.string.client_venue_leave_review),
            onClick = onReviewClick,
            type = ButtonType.Black,
            modifier = Modifier.fillMaxWidth()
        )
    }
}

@Composable
private fun ReviewsDesktopWorkStatus(venue: VenueResponse) {
    val status = remember(venue.schedule) { venue.currentWorkStatus() }
    val statusColor = if (status.isOpen) ActiveStatusStroke else AccentPink

    Row(
        horizontalArrangement = Arrangement.spacedBy(6.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .size(14.dp)
                .clip(CircleShape)
                .border(1.dp, statusColor, CircleShape),
            contentAlignment = Alignment.Center
        ) {
            Box(
                modifier = Modifier
                    .size(4.dp)
                    .clip(CircleShape)
                    .background(statusColor)
            )
        }

        Text(
            text = stringResource(
                if (status.isOpen) {
                    Res.string.client_venue_status_open
                } else {
                    Res.string.client_venue_closed
                }
            ),
            style = BodyMedium.copy(
                color = statusColor,
                fontSize = 14.sp,
                lineHeight = 21.sp,
                fontWeight = FontWeight.Medium,
                letterSpacing = (-0.15).sp
            )
        )

        val secondaryTime = if (status.isOpen) status.closeTime else status.nextOpenTime
        if (secondaryTime != null) {
            Text(
                text = "•",
                style = BodyMedium.copy(color = MineShaft400, fontSize = 14.sp, lineHeight = 21.sp)
            )

            Text(
                text = if (status.isOpen) {
                    stringResource(Res.string.client_venue_status_closes_at, secondaryTime.formatTime())
                } else {
                    stringResource(Res.string.client_venue_status_opens_at, secondaryTime.formatTime())
                },
                style = BodyMedium.copy(
                    color = MineShaft400,
                    fontSize = 14.sp,
                    lineHeight = 21.sp,
                    fontWeight = FontWeight.Medium,
                    textDecoration = androidx.compose.ui.text.style.TextDecoration.Underline
                ),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}

@Composable
private fun ReviewsDesktopRatingBlock(
    summary: ReviewSummaryResponse?,
    venue: VenueResponse,
    modifier: Modifier = Modifier
) {
    val details = summary?.averageDetails

    Column(modifier = modifier) {
        Text(
            text = stringResource(Res.string.client_reviews_rating_details),
            style = BodyMedium.copy(
                color = MineShaft950,
                fontSize = 16.sp,
                lineHeight = 24.sp,
                fontWeight = FontWeight.Medium,
                letterSpacing = (-0.31).sp
            )
        )

        Spacer(modifier = Modifier.height(18.dp))

        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(8.dp))
                .background(MineShaft50)
                .padding(horizontal = 24.dp, vertical = 26.dp),
            horizontalArrangement = Arrangement.spacedBy(56.dp),
            verticalAlignment = Alignment.Top
        ) {
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(14.dp)
            ) {
                ReviewMetricProgress(
                    label = stringResource(Res.string.client_review_price),
                    score = details?.price ?: venue.averageRating
                )
                ReviewMetricProgress(
                    label = stringResource(Res.string.client_review_quality),
                    score = details?.quality ?: venue.averageRating
                )
                ReviewMetricProgress(
                    label = stringResource(Res.string.client_review_speed),
                    score = details?.speed ?: venue.averageRating
                )
            }

            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(14.dp)
            ) {
                ReviewMetricProgress(
                    label = stringResource(Res.string.client_review_personal),
                    score = details?.staff ?: venue.averageRating
                )
                ReviewMetricProgress(
                    label = stringResource(Res.string.client_review_atmosphere),
                    score = details?.atmosphere ?: venue.averageRating
                )
            }
        }
    }
}

@Composable
private fun ReviewsTopBar(onBackClick: () -> Unit) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(28.dp)
    ) {
        Icon(
            painter = AppIcons.ArrowLeft,
            contentDescription = null,
            tint = MineShaft950,
            modifier = Modifier
                .align(Alignment.CenterStart)
                .size(24.dp)
                .clickable(onClick = onBackClick)
        )

        Text(
            text = stringResource(Res.string.client_review_title),
            style = BodyMedium.copy(
                color = MineShaft950,
                fontSize = 14.sp,
                lineHeight = 21.sp,
                fontWeight = FontWeight.Medium,
                textAlign = TextAlign.Center
            ),
            modifier = Modifier.align(Alignment.Center)
        )
    }
}

@Composable
private fun ReviewPhotoHero(
    photoUrls: List<String>,
    onPhotoClick: (String) -> Unit
) {
    BoxWithConstraints(
        modifier = Modifier.fillMaxWidth()
    ) {
        val hasSidePhotos = photoUrls.size > 1
        val galleryWidth = maxWidth
        val galleryHeight = galleryWidth * (204f / 327f)
        val gap = galleryWidth * (12f / 327f)
        val sideSize = galleryWidth * (96f / 327f)
        val mainWidth = if (hasSidePhotos) {
            galleryWidth - sideSize - gap
        } else {
            galleryWidth
        }

        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(galleryHeight),
            horizontalArrangement = Arrangement.spacedBy(gap)
        ) {
            ReviewImageTile(
                url = photoUrls.first(),
                onClick = onPhotoClick,
                modifier = Modifier
                    .width(mainWidth)
                    .height(galleryHeight)
            )

            if (hasSidePhotos) {
                Column(
                    modifier = Modifier.width(sideSize),
                    verticalArrangement = Arrangement.spacedBy(gap)
                ) {
                    photoUrls.drop(1).take(2).forEach { url ->
                        ReviewImageTile(
                            url = url,
                            onClick = onPhotoClick,
                            modifier = Modifier.size(sideSize)
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun VenueReviewsHeader(venue: VenueResponse) {
    val status = remember(venue.schedule) { venue.currentWorkStatus() }
    val location = buildString {
        append(venue.location.address)
        val city = venue.location.city?.takeIf(String::isNotBlank)
        if (city != null) {
            append(", ")
            append(city)
        }
    }

    Column(
        modifier = Modifier.fillMaxWidth()
    ) {
        Text(
            text = venue.name,
            style = BodyMedium.copy(
                color = MineShaft900,
                fontSize = 28.sp,
                lineHeight = 42.sp,
                fontWeight = FontWeight.Medium,
                letterSpacing = (-0.12).sp
            ),
            maxLines = 2,
            overflow = TextOverflow.Ellipsis
        )

        Spacer(modifier = Modifier.height(4.dp))

        Text(
            text = location,
            style = BodyMedium.copy(
                color = MineShaft600,
                fontSize = 14.sp,
                lineHeight = 24.sp,
                fontWeight = FontWeight.Normal,
                letterSpacing = (-0.31).sp
            ),
            maxLines = 2,
            overflow = TextOverflow.Ellipsis
        )

        Spacer(modifier = Modifier.height(8.dp))

        Row(
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(5.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    painter = AppIcons.RatingStarFilled,
                    contentDescription = null,
                    tint = Color.Unspecified,
                    modifier = Modifier.size(14.dp)
                )

                Text(
                    text = venue.ratingSummaryText(),
                    style = BodyMedium.copy(
                        color = MineShaft600,
                        fontSize = 13.sp,
                        lineHeight = 18.sp,
                        fontWeight = FontWeight.Normal
                    )
                )
            }

            Text(
                text = stringResource(
                    if (status.isOpen) {
                        Res.string.client_venue_status_open
                    } else {
                        Res.string.client_venue_closed
                    }
                ),
                style = BodyMedium.copy(
                    color = if (status.isOpen) ActiveStatusStroke else AccentPink,
                    fontSize = 14.sp,
                    lineHeight = 21.sp,
                    fontWeight = FontWeight.Medium,
                    letterSpacing = (-0.15).sp
                ),
                maxLines = 1
            )
        }
    }
}

@Composable
private fun ReviewRatingBlock(
    summary: ReviewSummaryResponse?,
    venue: VenueResponse
) {
    val details = summary?.averageDetails

    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Text(
            text = stringResource(Res.string.client_reviews_rating_details),
            style = BodyMedium.copy(
                color = MineShaft950,
                fontSize = 16.sp,
                lineHeight = 24.sp,
                fontWeight = FontWeight.Medium,
                letterSpacing = (-0.31).sp
            )
        )

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(8.dp))
                .background(MineShaft50)
                .padding(horizontal = 20.dp, vertical = 14.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            ReviewMetricProgress(
                label = stringResource(Res.string.client_review_price),
                score = details?.price ?: venue.averageRating
            )
            ReviewMetricProgress(
                label = stringResource(Res.string.client_review_quality),
                score = details?.quality ?: venue.averageRating
            )
            ReviewMetricProgress(
                label = stringResource(Res.string.client_review_speed),
                score = details?.speed ?: venue.averageRating
            )
            ReviewMetricProgress(
                label = stringResource(Res.string.client_review_personal),
                score = details?.staff ?: venue.averageRating
            )
            ReviewMetricProgress(
                label = stringResource(Res.string.client_review_atmosphere),
                score = details?.atmosphere ?: venue.averageRating
            )
        }
    }
}

@Composable
private fun ReviewMetricProgress(
    label: String,
    score: Double
) {
    val clampedScore = score.coerceIn(0.0, 10.0)
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(6.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = label,
                style = BodyMedium.copy(
                    color = MineShaft500,
                    fontSize = 11.sp,
                    lineHeight = 16.5.sp,
                    fontWeight = FontWeight.Normal
                ),
                maxLines = 1,
                softWrap = false
            )

            Text(
                text = "${clampedScore.roundOneDecimal()}/10",
                style = BodyMedium.copy(
                    color = MineShaft600,
                    fontSize = 10.sp,
                    lineHeight = 15.sp,
                    fontWeight = FontWeight.Medium,
                    textAlign = TextAlign.End
                ),
                maxLines = 1
            )
        }

        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(8.dp)
                .clip(RoundedCornerShape(100.dp))
                .background(MineShaft200)
        ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth((clampedScore / 10.0).toFloat())
                    .height(8.dp)
                    .clip(RoundedCornerShape(100.dp))
                    .background(Primary600.copy(alpha = 0.72f))
            )
        }
    }
}

@Composable
fun VenueReviewCard(
    review: ReviewResponse,
    isLikeLoading: Boolean,
    onPhotoClick: (String) -> Unit,
    onLikeClick: (String) -> Unit,
    modifier: Modifier = Modifier,
    isLikeEnabled: Boolean = true
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(MineShaft50)
            .padding(14.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.Top
        ) {
            Row(
                modifier = Modifier.weight(1f),
                horizontalArrangement = Arrangement.spacedBy(10.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                ReviewAuthorAvatar(review = review)

                Column(
                    modifier = Modifier.weight(1f)
                ) {
                    Text(
                        text = review.author?.displayName
                            ?: stringResource(Res.string.client_reviews_unknown_author),
                        style = BodyMedium.copy(
                            color = MineShaft900,
                            fontSize = 16.sp,
                            lineHeight = 24.sp,
                            fontWeight = FontWeight.Medium,
                            letterSpacing = (-0.31).sp
                        ),
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )

                    Text(
                        text = review.createdAt.formattedReviewDate(),
                        style = BodyMedium.copy(
                            color = MineShaft400,
                            fontSize = 12.sp,
                            lineHeight = 18.sp,
                            fontWeight = FontWeight.Normal
                        ),
                        maxLines = 1
                    )
                }
            }

            Row(
                horizontalArrangement = Arrangement.spacedBy(4.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    painter = AppIcons.RatingStarFilled,
                    contentDescription = null,
                    tint = Color.Unspecified,
                    modifier = Modifier.size(12.dp)
                )

                Text(
                    text = review.rating.toString(),
                    style = BodyMedium.copy(
                        color = MineShaft600,
                        fontSize = 11.sp,
                        lineHeight = 16.5.sp,
                        fontWeight = FontWeight.Medium
                    )
                )
            }
        }

        review.text?.takeIf(String::isNotBlank)?.let { text ->
            Spacer(modifier = Modifier.height(12.dp))

            Text(
                text = text,
                style = BodyMedium.copy(
                    color = MineShaft600,
                    fontSize = 15.sp,
                    lineHeight = 25.6f.sp,
                    fontWeight = FontWeight.Normal,
                    letterSpacing = 0.2.sp
                )
            )
        }

        ReviewInlinePhotos(
            photos = review.photos,
            onPhotoClick = onPhotoClick
        )

        review.ownerReply?.let { reply ->
            Spacer(modifier = Modifier.height(12.dp))
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(8.dp))
                    .background(White)
                    .border(1.dp, MineShaft100, RoundedCornerShape(8.dp))
                    .padding(10.dp)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(6.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(
                            painter = AppIcons.Shield,
                            contentDescription = null,
                            tint = MineShaft600,
                            modifier = Modifier.size(14.dp)
                        )
                        Text(
                            text = "Відповідь закладу",
                            style = BodyMedium.copy(
                                color = MineShaft900,
                                fontSize = 12.sp,
                                lineHeight = 18.sp,
                                fontWeight = FontWeight.Medium
                            )
                        )
                    }

                    Text(
                        text = reply.repliedAt.formattedReviewDate(),
                        style = BodyMedium.copy(
                            color = MineShaft400,
                            fontSize = 11.sp,
                            lineHeight = 16.5.sp,
                            fontWeight = FontWeight.Normal
                        )
                    )
                }

                Spacer(modifier = Modifier.height(8.dp))

                Text(
                    text = reply.text,
                    style = BodyMedium.copy(
                        color = MineShaft600,
                        fontSize = 13.sp,
                        lineHeight = 20.sp,
                        fontWeight = FontWeight.Normal
                    )
                )
            }
        }

        Spacer(modifier = Modifier.height(12.dp))

        Row(
            modifier = Modifier
                .clip(RoundedCornerShape(18.dp))
                .clickable(enabled = isLikeEnabled && !isLikeLoading) { onLikeClick(review.id) }
                .padding(horizontal = 2.dp, vertical = 2.dp),
            horizontalArrangement = Arrangement.spacedBy(5.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                painter = AppIcons.Like,
                contentDescription = null,
                tint = if (review.isLikedByMe) Color.Unspecified else MineShaft400,
                modifier = Modifier.size(18.dp)
            )

            Text(
                text = review.likesCount.compactCountText(),
                style = BodyMedium.copy(
                    color = MineShaft500,
                    fontSize = 11.sp,
                    lineHeight = 16.5.sp,
                    fontWeight = FontWeight.Medium
                )
            )
        }
    }
}

@Composable
private fun ReviewAuthorAvatar(review: ReviewResponse) {
    val avatarUrl = review.author?.avatarUrl?.takeIf(String::isNotBlank)

    Box(
        modifier = Modifier
            .size(36.dp)
            .clip(CircleShape)
            .background(MineShaft100),
        contentAlignment = Alignment.Center
    ) {
        if (avatarUrl != null) {
            AppNetworkImage(
                model = avatarUrl,
                contentDescription = review.author?.displayName,
                modifier = Modifier.fillMaxSize()
            )
        } else {
            Text(
                text = review.author?.displayName?.initials().orEmpty().ifBlank { "T" },
                style = BodyMedium.copy(
                    color = MineShaft600,
                    fontSize = 12.sp,
                    lineHeight = 18.sp,
                    fontWeight = FontWeight.Medium,
                    textAlign = TextAlign.Center
                )
            )
        }
    }
}

@Composable
private fun ReviewInlinePhotos(
    photos: List<ReviewMediaRef>,
    onPhotoClick: (String) -> Unit
) {
    val urls = photos.mapNotNull { it.publicUrl?.takeIf(String::isNotBlank) }
    if (urls.isEmpty()) return

    Spacer(modifier = Modifier.height(12.dp))

    LazyRow(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        items(
            items = urls,
            key = { it }
        ) { url ->
            ReviewImageTile(
                url = url,
                onClick = onPhotoClick,
                modifier = Modifier
                    .size(76.dp)
                    .aspectRatio(1f)
            )
        }
    }
}

@Composable
private fun ReviewImageTile(
    url: String,
    onClick: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    AppNetworkImage(
        model = url,
        contentDescription = null,
        modifier = modifier
            .clip(RoundedCornerShape(12.dp))
            .background(MineShaft100)
            .clickable { onClick(url) }
    )
}

@Composable
private fun ReviewPhotoPreviewDialog(
    url: String,
    onDismiss: () -> Unit
) {
    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false)
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(Color.Black.copy(alpha = 0.92f))
                .clickable(onClick = onDismiss),
            contentAlignment = Alignment.Center
        ) {
            AppNetworkImage(
                model = url,
                contentDescription = null,
                contentScale = ContentScale.Fit,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(18.dp)
            )

            Box(
                modifier = Modifier
                    .align(Alignment.TopEnd)
                    .padding(20.dp)
                    .size(38.dp)
                    .clip(CircleShape)
                    .background(Color.Black.copy(alpha = 0.35f))
                    .clickable(onClick = onDismiss),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    painter = AppIcons.Close,
                    contentDescription = null,
                    tint = White,
                    modifier = Modifier.size(20.dp)
                )
            }
        }
    }
}

private fun Instant.formattedReviewDate(): String {
    val date = toLocalDateTime(TimeZone.currentSystemDefault()).date
    val day = date.dayOfMonth.toString().padStart(2, '0')
    val month = date.monthNumber.toString().padStart(2, '0')
    return "$day.$month.${date.year}"
}

private fun String.initials(): String {
    return split(' ')
        .mapNotNull { it.firstOrNull()?.uppercaseChar()?.toString() }
        .take(2)
        .joinToString("")
}

private fun Double.roundOneDecimal(): String {
    val rounded = kotlin.math.round(this * 10.0) / 10.0
    val whole = rounded.toInt()
    val decimal = ((rounded - whole) * 10).toInt()
    return if (decimal == 0) whole.toString() else "$whole.$decimal"
}

private data class ReviewWorkStatus(
    val isOpen: Boolean,
    val closeTime: LocalTime?,
    val nextOpenTime: LocalTime?
)

private fun VenueResponse.currentWorkStatus(): ReviewWorkStatus {
    val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
    val day = now.date.dayOfWeek.toVenueDayOfWeek()
    val workDay = schedule.workDays.firstOrNull { it.day == day }
        ?: return ReviewWorkStatus(false, null, schedule.workDays.nextOpenTimeFrom(day, now.time))
    if (!workDay.isOpen) {
        return ReviewWorkStatus(false, null, schedule.workDays.nextOpenTimeFrom(day, now.time))
    }

    val openTime = workDay.openTime
    val closeTime = workDay.closeTime
    if (openTime == null || closeTime == null) return ReviewWorkStatus(true, closeTime, null)

    val nowMinutes = now.time.minutesOfDay()
    val openMinutes = openTime.minutesOfDay()
    val closeMinutes = closeTime.minutesOfDay()
    val isOpen = if (closeMinutes >= openMinutes) {
        nowMinutes in openMinutes..closeMinutes
    } else {
        nowMinutes >= openMinutes || nowMinutes <= closeMinutes
    }

    return ReviewWorkStatus(
        isOpen = isOpen,
        closeTime = closeTime.takeIf { isOpen },
        nextOpenTime = if (isOpen) null else schedule.workDays.nextOpenTimeFrom(day, now.time)
    )
}

@Suppress("REDUNDANT_ELSE_IN_WHEN")
private fun kotlinx.datetime.DayOfWeek.toVenueDayOfWeek(): VenueDayOfWeek = when (this) {
    kotlinx.datetime.DayOfWeek.MONDAY -> VenueDayOfWeek.MONDAY
    kotlinx.datetime.DayOfWeek.TUESDAY -> VenueDayOfWeek.TUESDAY
    kotlinx.datetime.DayOfWeek.WEDNESDAY -> VenueDayOfWeek.WEDNESDAY
    kotlinx.datetime.DayOfWeek.THURSDAY -> VenueDayOfWeek.THURSDAY
    kotlinx.datetime.DayOfWeek.FRIDAY -> VenueDayOfWeek.FRIDAY
    kotlinx.datetime.DayOfWeek.SATURDAY -> VenueDayOfWeek.SATURDAY
    kotlinx.datetime.DayOfWeek.SUNDAY -> VenueDayOfWeek.SUNDAY
    else -> VenueDayOfWeek.MONDAY
}

private fun LocalTime.minutesOfDay(): Int = hour * 60 + minute

private fun LocalTime.formatTime(): String {
    return "${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}"
}

private fun VenueDayOfWeek.sortIndex(): Int {
    return ReviewVenueDays.indexOf(this)
}

private fun VenueDayOfWeek.plusDays(days: Int): VenueDayOfWeek {
    val index = (sortIndex() + days) % ReviewVenueDays.size
    return ReviewVenueDays[index]
}

private fun List<com.patronat.top.dto.venues.WorkDay>.nextOpenTimeFrom(
    currentDay: VenueDayOfWeek,
    currentTime: LocalTime
): LocalTime? {
    val currentMinutes = currentTime.minutesOfDay()
    for (offset in 0 until ReviewVenueDays.size) {
        val day = currentDay.plusDays(offset)
        val workDay = firstOrNull { it.day == day }
        val openTime = workDay?.openTime
        if (workDay?.isOpen != true || openTime == null) continue

        if (offset > 0 || openTime.minutesOfDay() > currentMinutes) {
            return openTime
        }
    }

    return null
}

private val ReviewVenueDays = listOf(
    VenueDayOfWeek.MONDAY,
    VenueDayOfWeek.TUESDAY,
    VenueDayOfWeek.WEDNESDAY,
    VenueDayOfWeek.THURSDAY,
    VenueDayOfWeek.FRIDAY,
    VenueDayOfWeek.SATURDAY,
    VenueDayOfWeek.SUNDAY
)
